import {useState} from "react";
import {Outlet} from "react-router";
import {Box, Divider, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, styled, useTheme} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import {ChevronLeft, Menu} from "@mui/icons-material";
import {isUserAdmin} from "../utils";

export const NavigationDrawer = ({navOptions, currentNavOption, handleNavigate, currentLeague, currentSeason, currentAuction, refreshLeagueMetadata}) => {
    const theme = useTheme();
    const dims = theme.dimensions;
    const [navDrawerOpen, setNavDrawerOpen] = useState(true);

    const handleNavDrawerOpen = () => {
        setNavDrawerOpen(true);
    };

    const handleNavDrawerClose = () => {
        setNavDrawerOpen(false);
    };

    const openedMixin = (theme) => ({
        width: dims.navDrawerWidth,
        height: `calc(100% - ${dims.headerHeight + dims.footerHeight}px)`,
        border: theme.palette.contentPanel.border,
        marginTop: dims.headerHeight,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        overflowX: 'hidden',
    });

    const closedMixin = (theme) => ({
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
        width: `calc(${theme.spacing(7)} + 1px)`,
        height: `calc(100% - ${dims.headerHeight + dims.footerHeight}px)`,
        border: theme.palette.contentPanel.border,
        marginTop: dims.headerHeight,
        [theme.breakpoints.up('sm')]: {
            width: `calc(${theme.spacing(8)} + 1px)`,
        },
    });

    const DrawerHeader = styled('div')(({ theme }) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginTop: 8,
        marginBottom: 8
    }));

    const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'navDrawerOpen' })(({ theme }) => ({
            width: dims.navDrawerWidth,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
            variants: [
                {
                    props: ({ open }) => navDrawerOpen,
                    style: {
                        ...openedMixin(theme),
                        '& .MuiDrawer-paper': openedMixin(theme),
                    },
                },
                {
                    props: ({ open }) => !navDrawerOpen,
                    style: {
                        ...closedMixin(theme),
                        '& .MuiDrawer-paper': closedMixin(theme),
                    },
                },
            ],
        }),
    );

    return (
        <div style={{width: '100%'}}>
            <Box sx={{display: 'flex'}}>
                <Drawer variant="permanent" open={navDrawerOpen} anchor="top">
                    <DrawerHeader>
                        {!navDrawerOpen &&
                            <IconButton onClick={handleNavDrawerOpen} style={{marginRight: 12}}>
                                <Menu/>
                            </IconButton>
                        }
                        {navDrawerOpen &&
                            <IconButton onClick={handleNavDrawerClose} style={{marginRight: 12}}>
                                <ChevronLeft/>
                            </IconButton>
                        }
                    </DrawerHeader>
                    <Divider/>
                    <List>
                        {navOptions.map((optionDef) => (
                            ((!optionDef.adminOnly) || (optionDef.adminOnly && isUserAdmin())) &&
                            ((!optionDef.phases) || (optionDef.phases.includes(currentLeague.season_phase))) &&
                            <div key={optionDef.id}>
                                {optionDef.divider && <Divider style={{marginTop: 10, marginBottom: 10}}/>}
                                {!optionDef.divider &&
                                    <ListItem key={optionDef.label} disablePadding sx={{display: 'block'}} onClick={() => handleNavigate(optionDef)}>
                                        <ListItemButton sx={[{minHeight: 48, px: 2.5}, navDrawerOpen ? {justifyContent: 'initial'} : {justifyContent: 'center'}]}
                                                        selected={currentNavOption === optionDef}>
                                            <ListItemIcon sx={[{minWidth: 0, justifyContent: 'center'}, navDrawerOpen ? {mr: 3} : {mr: 'auto'}]}>
                                                {optionDef.icon}
                                            </ListItemIcon>
                                            <ListItemText primary={optionDef.label} sx={[navDrawerOpen ? {opacity: 1} : {opacity: 0}]}/>
                                        </ListItemButton>
                                    </ListItem>
                                }
                            </div>
                        ))}
                    </List>
                    {navDrawerOpen && isUserAdmin() &&
                        <div style={{position: 'fixed', bottom: dims.footerHeight + 10, left: 20, fontSize: 12}}>
                            <pre>Season: {currentSeason.year}</pre>
                            <pre>Week:   {currentAuction.week_num}</pre>
                            <pre>Phase:  {currentLeague.season_phase}</pre>
                        </div>
                    }
                </Drawer>
                <Box component="main" sx={{flexGrow: 1, p: 3, padding: 0}}>
                    <Outlet context={{currentLeague, currentSeason, currentAuction, refreshLeagueMetadata}}/>
                </Box>
            </Box>
        </div>
    );
};